import { useState } from "react"

import type {
  SolarProposalRebatesDto,
  UpdateSolarProposalRebatesDto,
} from "~/api/dto/solar-proposal-pricing-steps.dto"
import useSolarProposalRebates from "~/api/hooks/useSolarProposalRebates"
import useUpdateSolarProposalRebates from "~/api/hooks/useUpdateSolarProposalRebates"
import { TextField, ToggleField } from "~/components/ui-components/v2"

import { describeCommandError } from "../ProposalSetup/pricing/describe-command-error"
import type { PricingStepCompanion } from "../ProposalSetup/pricing/pricing-kind-bundle"

export const HOMEOWNER_REBATE_MAX = 3000
export const HOMEOWNER_REBATE_TOO_HIGH = `The homeowner rebate can't be more than $3,000.`

type RebateKind =
  | `energyEfficiencyKit`
  | `homeowner`
  | `roofWork`
  | `treeTrimming`

const REBATES: ReadonlyArray<{ kind: RebateKind; label: string }> = [
  { kind: `homeowner`, label: `Homeowner rebate` },
  { kind: `roofWork`, label: `Roof work rebate` },
  { kind: `energyEfficiencyKit`, label: `Energy-efficiency kit rebate` },
  { kind: `treeTrimming`, label: `Tree trimming rebate` },
]

/** Each rebate's toggle and the amount as typed. */
type RebatesDraft = Readonly<
  Record<RebateKind, { readonly on: boolean; readonly amount: string }>
>

const draftOf = (rebates: SolarProposalRebatesDto): RebatesDraft => ({
  homeowner: {
    on: rebates.homeownerRebate,
    amount: String(rebates.homeownerRebateAmount),
  },
  roofWork: {
    on: rebates.roofWorkRebate,
    amount: String(rebates.roofWorkRebateAmount),
  },
  energyEfficiencyKit: {
    on: rebates.energyEfficiencyKitRebate,
    amount: String(rebates.energyEfficiencyKitRebateAmount),
  },
  treeTrimming: {
    on: rebates.treeTrimmingRebate,
    amount: String(rebates.treeTrimmingRebateAmount),
  },
})

/** An empty amount is 0; the server refuses a toggled-on rebate without one. */
const amountOf = (value: string): number =>
  value.trim() === `` ? 0 : Number(value)

const bodyOf = (draft: RebatesDraft): UpdateSolarProposalRebatesDto => ({
  homeownerRebate: draft.homeowner.on,
  homeownerRebateAmount: amountOf(draft.homeowner.amount),
  roofWorkRebate: draft.roofWork.on,
  roofWorkRebateAmount: amountOf(draft.roofWork.amount),
  energyEfficiencyKitRebate: draft.energyEfficiencyKit.on,
  energyEfficiencyKitRebateAmount: amountOf(draft.energyEfficiencyKit.amount),
  treeTrimmingRebate: draft.treeTrimming.on,
  treeTrimmingRebateAmount: amountOf(draft.treeTrimming.amount),
})

const sameBody = (
  a: UpdateSolarProposalRebatesDto,
  b: UpdateSolarProposalRebatesDto,
): boolean =>
  REBATES.every(({ kind }) => {
    const on = `${kind}Rebate` as const
    const amount = `${kind}RebateAmount` as const
    return a[on] === b[on] && a[amount] === b[amount]
  })

const SolarRebatesSection = ({
  draft,
  disabled,
  noActiveDesign,
  loadError,
  homeownerError,
  saveError,
  onChange,
}: {
  draft: RebatesDraft | null
  disabled: boolean
  noActiveDesign: boolean
  loadError: string | null
  homeownerError: string | null
  saveError: string | null
  onChange: (kind: RebateKind, next: { on: boolean; amount: string }) => void
}): JSX.Element => (
  <section
    aria-labelledby="solar-rebates-heading"
    data-testid="solar-rebates"
    className="flex flex-col gap-3 border-t border-border pt-5"
  >
    <h3
      id="solar-rebates-heading"
      className="font-display text-[length:var(--step-0)] font-semibold text-foreground"
    >
      Rebates
    </h3>
    {loadError ? (
      <p role="alert" className="text-[length:var(--step--2)] text-danger">
        {loadError}
      </p>
    ) : null}
    {noActiveDesign ? (
      <p role="note" className="text-[length:var(--step--2)] text-muted">
        Rebates apply to the active design. Choose a design first.
      </p>
    ) : null}
    {draft ? (
      <ul className="grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2">
        {REBATES.map(({ kind, label }) => {
          const rebate = draft[kind]
          return (
            <li
              key={kind}
              className="flex flex-col gap-2 rounded-lg border border-bp-border bg-bp-elevated p-4"
            >
              <ToggleField
                label={label}
                checked={rebate.on}
                disabled={disabled}
                onChange={(on: boolean) => onChange(kind, { ...rebate, on })}
              />
              {rebate.on ? (
                <TextField
                  label={`${label} amount`}
                  type="number"
                  min={0}
                  max={kind === `homeowner` ? HOMEOWNER_REBATE_MAX : undefined}
                  step={1}
                  leadingAddon="$"
                  disabled={disabled}
                  value={rebate.amount}
                  error={kind === `homeowner` ? homeownerError : undefined}
                  onChange={(event) =>
                    onChange(kind, { ...rebate, amount: event.target.value })
                  }
                />
              ) : null}
            </li>
          )
        })}
      </ul>
    ) : null}
    {saveError ? (
      <p role="alert" className="text-[length:var(--step--2)] text-danger">
        {saveError}
      </p>
    ) : null}
  </section>
)

/**
 * The Adders drawer's Rebates: four toggles with amounts, saved by the
 * drawer's Save after the adders. The homeowner rebate is capped at $3,000
 * here as on the server; any other refusal shows the server's reason.
 */
export const useSolarRebatesCompanion = (
  proposalId: string,
  { disabled }: { readonly disabled: boolean },
): PricingStepCompanion => {
  const query = useSolarProposalRebates(proposalId)
  const update = useUpdateSolarProposalRebates()
  const [userDraft, setUserDraft] = useState<RebatesDraft | null>(null)
  const saved = query.data
  const draft = userDraft ?? (saved ? draftOf(saved) : null)
  const noActiveDesign = saved?.noActiveDesign === true
  const body = draft ? bodyOf(draft) : null
  const dirty =
    body != null && saved != null && !sameBody(body, bodyOf(draftOf(saved)))
  const homeownerTooHigh =
    body != null &&
    body.homeownerRebate &&
    body.homeownerRebateAmount > HOMEOWNER_REBATE_MAX
  const invalidAmount =
    body != null &&
    REBATES.some(
      ({ kind }) => !Number.isFinite(body[`${kind}RebateAmount` as const]),
    )

  return {
    dirty,
    blocked: dirty && (homeownerTooHigh || invalidAmount),
    saving: update.isLoading,
    save: ({ onSuccess, onError }) => {
      if (body == null) {
        onSuccess()
        return
      }
      update.mutate(
        { proposalId, body },
        {
          onSuccess: () => {
            setUserDraft(null)
            onSuccess()
          },
          onError,
        },
      )
    },
    section: (
      <SolarRebatesSection
        draft={draft}
        disabled={disabled || update.isLoading || noActiveDesign}
        noActiveDesign={noActiveDesign}
        loadError={
          query.isError
            ? describeCommandError(query.error, `Unable to load rebates`)
            : null
        }
        homeownerError={homeownerTooHigh ? HOMEOWNER_REBATE_TOO_HIGH : null}
        saveError={
          update.isError
            ? describeCommandError(update.error, `Unable to save rebates`)
            : null
        }
        onChange={(kind, next) => {
          if (draft == null) return
          update.reset()
          setUserDraft({ ...draft, [kind]: next })
        }}
      />
    ),
  }
}
